/* Category controls for the complete, server-rendered iPad catalog. */
(() => {
  'use strict';
  if (customElements.get('avontic-ipad-shop')) return;
  const modes = new Set(['featured', 'all', 'bundles', 'cases', 'accessories']);
  const aliases = {'#av-ipad-bundles':'bundles','#av-ipad-cases':'cases','#avontic-products':'all','#av-ipad-featured':'featured'};
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  class IpadShop extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.catalog = this.querySelector('[data-ipad-catalog]');
      if (!this.catalog) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.search = this.querySelector('[data-ipad-search]');
      this.sort = this.querySelector('[data-ipad-sort]');
      this.heading = this.querySelector('[data-catalog-heading]');
      this.status = this.querySelector('[data-ipad-status]');
      this.mode = 'featured';
      this.refreshCards();
      this.querySelector('[data-ipad-tools]').hidden = false;
      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('[data-ipad-mode]');
        if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
          event.preventDefault();
          event.stopPropagation();
          this.select(link.dataset.ipadMode, true, true);
          return;
        }
        if (event.target.closest('[data-ipad-clear]')) {
          this.search.value = '';
          this.apply();
          this.search.focus();
        }
        if (event.target.closest('[data-ipad-next]') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          if (!this.loading) this.loadRemaining();
        }
      }, {signal, capture: true});
      this.search.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.apply(), 100);
      }, {signal});
      this.sort.addEventListener('change', () => this.apply(), {signal});
      const restore = () => this.select(this.hashMode(), false, false);
      window.addEventListener('popstate', restore, {signal});
      window.addEventListener('hashchange', restore, {signal});
      this.select(this.hashMode(), false, false);
      this.loadRemaining();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.searchTimer);
    }
    hashMode() {
      if (aliases[location.hash]) return aliases[location.hash];
      const suffix = location.hash.replace('#av-ipad-catalog-', '');
      return modes.has(suffix) ? suffix : 'featured';
    }
    refreshCards() {
      this.groups = Array.from(this.querySelectorAll('[data-ipad-group]'));
      this.cards = Array.from(this.querySelectorAll('[data-ipad-card]'));
      this.cards.forEach((card, index) => {
        card.ipadSearch = normalize(card.dataset.search);
        card.ipadIndex = index;
      });
      const counts = {all: this.cards.length, bundles: 0, cases: 0, accessories: 0};
      this.cards.forEach(card => { if (card.dataset.kind in counts) counts[card.dataset.kind]++; });
      this.querySelectorAll('[data-ipad-count]').forEach(node => { node.textContent = String(counts[node.dataset.ipadCount] ?? 0); });
    }
    select(mode, push, scroll) {
      this.mode = modes.has(mode) ? mode : 'all';
      this.dataset.view = this.mode;
      this.search.value = '';
      this.sort.value = 'recommended';
      this.querySelector('[data-ipad-editorial]').hidden = this.mode !== 'featured';
      this.querySelectorAll('[data-ipad-mode]').forEach(link => {
        const active = link.dataset.ipadMode === this.mode || (this.mode === 'featured' && link.closest('.av-shop__tabs') && link.dataset.ipadMode === 'all');
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      this.apply();
      if (push) {
        const url = new URL(location.href);
        url.hash = this.mode === 'featured' ? 'av-ipad-featured' : `av-ipad-catalog-${this.mode}`;
        history.pushState(history.state, '', url);
      }
      if (scroll) {
        requestAnimationFrame(() => {
          const target = this.mode === 'featured' ? this.querySelector('#av-ipad-featured') : this.catalog;
          if (this.mode !== 'featured') this.heading.focus({preventScroll:true});
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block:'start'});
        });
      }
    }
    apply() {
      const kind = this.mode === 'featured' ? 'all' : this.mode;
      const tokens = normalize(this.search.value).split(' ').filter(Boolean);
      const sortBy = this.sort.value;
      let visible = 0;
      this.heading.textContent = this.dataset[`title${kind.charAt(0).toUpperCase()}${kind.slice(1)}`] || this.dataset.titleAll;
      this.groups.forEach(group => {
        const grid = group.querySelector('[data-ipad-grid]');
        const cards = Array.from(grid.querySelectorAll('[data-ipad-card]'));
        cards.sort((a,b) => {
          if (sortBy === 'price-up') return Number(a.dataset.price)-Number(b.dataset.price) || a.ipadIndex-b.ipadIndex;
          if (sortBy === 'price-down') return Number(b.dataset.price)-Number(a.dataset.price) || a.ipadIndex-b.ipadIndex;
          if (sortBy === 'name') return a.dataset.title.localeCompare(b.dataset.title, this.dataset.language || undefined);
          return Number(a.dataset.rank)-Number(b.dataset.rank) || a.ipadIndex-b.ipadIndex;
        });
        let groupCount = 0;
        cards.forEach(card => {
          const match = (kind === 'all' || card.dataset.kind === kind) && tokens.every(token => card.ipadSearch.includes(token));
          card.hidden = !match;
          if (match) { visible++; groupCount++; }
          grid.appendChild(card);
        });
        group.hidden = groupCount === 0;
      });
      this.querySelector('[data-ipad-empty]').hidden = visible !== 0;
      this.status.textContent = this.dataset.loaded.replace('__COUNT__', String(visible)) + (this.loading ? ` · ${this.dataset.loading}` : '');
    }
    async loadRemaining() {
      let next = this.catalog.querySelector('[data-ipad-next]');
      const oldPage = Number(this.catalog.dataset.page) > 1;
      if ((!next && !oldPage) || this.loading) return;
      this.loading = true;
      this.querySelector('[data-ipad-error]').hidden = true;
      this.apply();
      try {
        const visited = new Set();
        let nextUrl = oldPage ? location.href : next.href;
        let first = oldPage;
        while (nextUrl) {
          const url = new URL(nextUrl, location.href);
          if (url.origin !== location.origin) throw new Error('Invalid pagination origin');
          if (first) { url.searchParams.set('page', '1'); first = false; }
          url.searchParams.set('section_id', this.catalog.dataset.sectionId);
          const view = new URL(location.href).searchParams.get('view');
          if (view) url.searchParams.set('view', view);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          url.hash = '';
          if (visited.has(url.href)) throw new Error('Repeated pagination URL');
          visited.add(url.href);
          const response = await fetch(url.href, {credentials:'same-origin', signal:this.controller.signal});
          if (!response.ok) throw new Error('Catalog page unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incoming = doc.querySelector('[data-ipad-catalog]');
          if (!incoming) throw new Error('Catalog markup missing');
          const ids = new Set(this.cards.map(card => card.dataset.productId));
          incoming.querySelectorAll('[data-ipad-card]').forEach(card => {
            if (ids.has(card.dataset.productId)) return;
            const group = this.groups.find(group => group.dataset.ipadGroup === card.dataset.kind);
            if (group) { group.querySelector('[data-ipad-grid]').appendChild(document.importNode(card, true)); ids.add(card.dataset.productId); }
          });
          const incomingNext = incoming.querySelector('[data-ipad-next]');
          nextUrl = incomingNext?.getAttribute('href') || '';
          if (nextUrl) {
            if (!next) { next = document.importNode(incomingNext, true); this.catalog.appendChild(next); }
            next.href = new URL(nextUrl, location.href).href;
          } else { next?.remove(); next = null; }
          this.catalog.dataset.page = '1';
          this.refreshCards();
          this.apply();
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.querySelector('[data-ipad-error]').hidden = false;
      } finally {
        this.loading = false;
        if (this.isConnected) this.apply();
      }
    }
  }
  customElements.define('avontic-ipad-shop', IpadShop);
})();
